import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Crown, Stethoscope } from "lucide-react";
import Layout from "@/components/Layout";
import { api, vibrate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PERKS = {
  dmd_launch: ["Analyses illimitées", "1 relation suivie", "Red flags, Rizz Score, Verdict psy & réponses"],
  relation_basic: ["Analyses illimitées", "1 relation suivie", "Red flags, Rizz Score & réponses"],
  relation_pro: ["Tout Basic, +", "Relations illimitées", "Suivi WhatsApp 24/7", "Alertes de dégradation"],
  relation_docteur: ["Tout Pro, +", "Consultations vocales avec ton coach IA", "Plans d'action multi-semaines", "Check-ins de suivi"],
};

const META = {
  dmd_launch: { name: "Lancement", sub: "Analyses illimitées", badge: "OFFRE DE LANCEMENT" },
  relation_basic: { name: "Basic", sub: "Pour tester sérieusement" },
  relation_pro: { name: "Pro", sub: "Le suivi complet", badge: "POPULAIRE" },
  relation_docteur: { name: "Docteur", sub: "Ton coach personnel" },
};

function fmt(amount, currency) {
  const v = (amount / 100).toFixed(2).replace(".", ",").replace(",00", "");
  return `${v}${currency === "eur" ? "€" : ` ${currency}`}`;
}

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    api.get("/payments/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const checkout = async (lookup_key) => {
    vibrate();
    setLoading(lookup_key);
    try {
      const res = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        user_id: user?.user_id,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Impossible d'ouvrir le paiement.");
      setLoading(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500 to-purple-600 flex items-center justify-center shadow-[0_0_30px_rgba(225,29,72,0.4)] mb-4 animate-float">
            <Crown className="text-white" size={28} />
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Passe premium</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1.5">Ton <span className="text-gradient">Docteur Love</span></h1>
          <p className="text-slate-400 text-sm mt-2">Comprends, protège, améliore ta relation.</p>
        </div>

        <div className="space-y-3">
          {plans.map((plan) => {
            const m = META[plan.lookup_key] || {};
            const launch = plan.lookup_key === "dmd_launch";
            const best = plan.lookup_key === "relation_pro";
            const perks = PERKS[plan.lookup_key] || [];
            const oldPrice = launch && plan.metadata?.display_price
              ? `${String(plan.metadata.display_price).replace(".", ",").replace(",00", "")}€`
              : null;
            return (
              <motion.button
                key={plan.lookup_key}
                data-testid={`plan-${plan.lookup_key}`}
                whileTap={{ scale: 0.98 }}
                onClick={() => checkout(plan.lookup_key)}
                disabled={loading}
                className={`relative w-full text-left rounded-2xl p-5 transition-all ${
                  launch
                    ? "bg-gradient-to-br from-amber-500/15 to-rose-500/10 border border-amber-400/50 shadow-[0_0_28px_rgba(245,158,11,0.25)]"
                    : best
                    ? "bg-gradient-to-br from-rose-500/15 to-purple-500/10 border border-rose-400/40 shadow-[0_0_28px_rgba(225,29,72,0.22)]"
                    : "glass-card hover:border-white/20"
                }`}
              >
                {m.badge && (
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-widest text-white rounded-full px-2.5 py-0.5 mb-2 ${
                    launch ? "bg-gradient-to-r from-amber-500 to-rose-500" : "bg-gradient-to-r from-rose-500 to-purple-500"
                  }`}>
                    {launch ? "🚀" : "⭐"} {m.badge}
                  </span>
                )}
                <div className="flex items-end justify-between">
                  <div>
                    <p className="font-display font-bold text-lg">{m.name}</p>
                    <p className="text-xs text-slate-500">{m.sub}</p>
                  </div>
                  <div className="text-right">
                    {oldPrice && (
                      <span data-testid="plan-old-price" className="block text-sm text-slate-500 line-through leading-none mb-0.5">{oldPrice}</span>
                    )}
                    <span className="font-display text-3xl font-extrabold">
                      {fmt(plan.amount, plan.currency)}
                    </span>
                    <span className="text-slate-500 text-sm">/mois</span>
                  </div>
                </div>
                <ul className="mt-3 space-y-1.5">
                  {perks.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-xs text-slate-300">
                      <Check size={13} className="text-rose-400 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
                {loading === plan.lookup_key && (
                  <p className="text-xs text-rose-400 mt-2">Ouverture du paiement…</p>
                )}
              </motion.button>
            );
          })}

          {/* Docteur — shown as "coming soon" only until the Stripe price (relation_docteur) exists */}
          {!plans.some((p) => p.lookup_key === "relation_docteur") && (
          <div data-testid="plan-docteur" className="w-full text-left rounded-2xl p-5 glass-card opacity-90">
            <span className="inline-block text-[10px] font-bold tracking-widest text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full mb-2">BIENTÔT</span>
            <div className="flex items-end justify-between">
              <div>
                <p className="font-display font-bold text-lg flex items-center gap-1"><Stethoscope size={16} className="text-purple-400" /> Docteur</p>
                <p className="text-xs text-slate-500">Ton coach personnel</p>
              </div>
              <div className="text-right">
                <span className="font-display text-3xl font-extrabold text-slate-400">29,99€</span>
                <span className="text-slate-500 text-sm">/mois</span>
              </div>
            </div>
            <ul className="mt-3 space-y-1.5">
              <li className="flex items-center gap-2 text-xs text-slate-400"><Check size={13} className="text-purple-400 shrink-0" /> Tout Pro, +</li>
              <li className="flex items-center gap-2 text-xs text-slate-400"><Check size={13} className="text-purple-400 shrink-0" /> Consultations vocales avec ton coach IA</li>
              <li className="flex items-center gap-2 text-xs text-slate-400"><Check size={13} className="text-purple-400 shrink-0" /> Plans d'action multi-semaines & check-ins</li>
            </ul>
          </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-600">
          Paiement sécurisé Stripe · Apple Pay & Google Pay · Annulable à tout moment
        </p>
      </div>
    </Layout>
  );
}
